import type { JSX, ComponentChildren } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { useIsWide } from '../lib/useIsWide';
import { state, slug, upsertRecipe, archiveRecipe, restoreRecipe } from '../store/store';
import { showToast } from '../store/toast';
import { Layout } from '../components/Layout';
import { RecipeForm } from '../components/RecipeForm';
import { ComponentPills } from '../components/ComponentPills';
import { StatusTag } from '../components/StatusTag';
import { PremadeChip } from '../components/PremadeChip';
import { EmojiBadge } from '../components/EmojiBadge';
import { RecipePhotoEditor } from '../components/RecipePhotoEditor';
import { navigate, backOr } from '../router';
import { formatMinutes } from '../lib/format';
import { AddToPlanModal } from '../components/AddToPlanModal';
import { todayIso, weekIdOf } from '../lib/dates';

function parsePos(pos: string | null | undefined): [number, number] {
  if (!pos) return [50, 65];
  const m = /^(\d+)%\s+(\d+)%$/.exec(pos);
  return m ? [Number(m[1]), Number(m[2])] : [50, 65];
}

export function RecipeDetailPage(props: { id: string }) {
  const s = state.value!;
  const isNew = props.id === 'new';
  const recipe = isNew ? undefined : s.recipes.find(r => r.id === props.id);
  const [editing, setEditing] = useState(isNew);
  const [planOpen, setPlanOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [previewFocus, setPreviewFocus] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number; rect: DOMRect; moved: boolean } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isWide = useIsWide();

  useEffect(() => {
    if (!focusMode) {
      setPreviewFocus(null);
      setDragging(false);
      dragRef.current = null;
    }
  }, [focusMode]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onMouseDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, [menuOpen]);

  const currentWeekId = weekIdOf(todayIso());

  if (!isNew && !recipe) {
    return (
      <Layout active="recipes" slug={slug.value}>
        <div style={{ padding: 24 }}>Recipe not found. <a href={`/h/${slug.value}/recipes`} onClick={(e) => { e.preventDefault(); navigate(`/h/${slug.value}/recipes`); }}>Back</a></div>
      </Layout>
    );
  }

  if (editing || isNew) {
    return (
      <Layout active="recipes" slug={slug.value}>
        <div style={{ padding: 16, maxWidth: 760, margin: '0 auto' }}>
          <h2>{isNew ? 'New recipe' : `Edit · ${recipe!.name}`}</h2>
          <RecipeForm
            initial={recipe}
            onSave={async r => { await upsertRecipe(r); navigate(`/h/${slug.value}/recipes/${r.id}`); }}
            onCancel={() => isNew ? navigate(`/h/${slug.value}/recipes`) : setEditing(false)}
            onDelete={isNew ? undefined : async () => { await archiveRecipe(recipe!.id); showToast(`Archived "${recipe!.name}"`, { label: 'Undo', onClick: () => { void restoreRecipe(recipe!.id); } }); navigate(`/h/${slug.value}/recipes`); }}
          />
        </div>
      </Layout>
    );
  }

  const r = recipe!;
  const imageHeight = isWide ? 520 : 220;

  const cuisineLabel = r.cuisine
    ? r.cuisine.charAt(0).toUpperCase() + r.cuisine.slice(1)
    : null;
  const categoryLabel = r.category
    ? r.category.replace(/-/g, ' ')
    : null;
  const cuisineCategory = [cuisineLabel, categoryLabel].filter(Boolean).join(' / ');

  const stats: Array<{ label: string; value: ComponentChildren }> = [];
  if (r.prep_minutes != null && r.prep_minutes > 0) {
    stats.push({ label: 'Prep', value: formatMinutes(r.prep_minutes) });
  }
  if (r.cook_minutes != null && r.cook_minutes > 0) {
    stats.push({ label: 'Cook', value: formatMinutes(r.cook_minutes) });
  }
  if (r.servings != null && r.servings > 0) {
    stats.push({ label: 'Serves', value: String(r.servings) });
  }
  if (cuisineCategory) {
    stats.push({ label: 'Style', value: cuisineCategory });
  }
  const hasComponents = Object.values(r.components).some(Boolean);
  if (hasComponents) {
    stats.push({
      label: 'Has',
      value: <ComponentPills components={r.components} hiddenVeggie={r.hidden_veggie} />,
    });
  }

  const sourceLink = r.source_url ? (
    <a
      href={r.source_url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-block',
        padding: '8px 14px', borderRadius: 'var(--radius-md)',
        background: 'var(--pink-tint)', color: 'var(--pink-primary)',
        border: '1px solid var(--pink-soft-bg)',
        fontSize: 13, fontWeight: 600, textDecoration: 'none',
      }}
    >
      🔗 {r.source_name ? `View on ${r.source_name}` : 'View original recipe'}
    </a>
  ) : null;

  const onPointerDown = (e: PointerEvent) => {
    if (!focusMode) return;
    const target = e.currentTarget as HTMLDivElement;
    target.setPointerCapture(e.pointerId);
    const [bx, by] = parsePos(r.image_focus);
    const rect = target.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: bx, baseY: by, rect, moved: false };
    setDragging(true);
    e.preventDefault();
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!dragRef.current) return;
    dragRef.current.moved = true;
    const { startX, startY, baseX, baseY, rect } = dragRef.current;
    const dxPct = ((e.clientX - startX) / rect.width) * 100;
    const dyPct = ((e.clientY - startY) / rect.height) * 100;
    const x = Math.max(0, Math.min(100, Math.round(baseX - dxPct)));
    const y = Math.max(0, Math.min(100, Math.round(baseY - dyPct)));
    setPreviewFocus(`${x}% ${y}%`);
  };

  const onPointerUp = async (e: PointerEvent) => {
    if (!dragRef.current) return;
    const { moved } = dragRef.current;
    dragRef.current = null;
    const finalFocus = previewFocus;
    setDragging(false);
    if (moved && finalFocus && finalFocus !== r.image_focus) {
      await upsertRecipe({ ...r, image_focus: finalFocus });
    }
    setFocusMode(false);
    setPreviewFocus(null);
  };

  const onPointerCancel = () => {
    dragRef.current = null;
    setDragging(false);
    setFocusMode(false);
    setPreviewFocus(null);
  };

  const photoBlock = (
    <div
      className={`detail-photo-wrap${focusMode ? ' focus-mode' : ''}`}
      style={{
        height: imageHeight,
        cursor: focusMode ? (dragging ? 'grabbing' : 'grab') : undefined,
        touchAction: focusMode ? 'none' : undefined,
      }}
      onPointerDown={focusMode ? (onPointerDown as any) : undefined}
      onPointerMove={focusMode ? (onPointerMove as any) : undefined}
      onPointerUp={focusMode ? (onPointerUp as any) : undefined}
      onPointerCancel={focusMode ? (onPointerCancel as any) : undefined}
    >
      <div style={{ height: '100%' }}>
        <EmojiBadge category={r.category} cuisine={r.cuisine} imageUrl={r.image_url} imageFocus={focusMode && previewFocus ? previewFocus : r.image_focus} height={imageHeight} />
      </div>
      <RecipePhotoEditor
        imageUrl={r.image_url}
        onChange={async url => { await upsertRecipe({ ...r, image_url: url, image_focus: null }); }}
        focusMode={focusMode}
        onToggleFocusMode={() => setFocusMode(v => !v)}
      />
    </div>
  );

  const actionButtons = (
    <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
      <button onClick={() => setPlanOpen(true)} style={btn}>+ Add to plan</button>
      {sourceLink}
      <div style={{ marginLeft: 'auto', position: 'relative' }} ref={menuRef}>
        <button
          onClick={() => setMenuOpen(v => !v)}
          aria-label="More actions"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          style={{
            ...secBtn, width: 44, padding: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, letterSpacing: 0,
          }}
        >⋮</button>
        {menuOpen && (
          <div
            role="menu"
            style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 6,
              background: 'white', borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lift)', border: '1px solid var(--hairline)',
              minWidth: 180, padding: 4, zIndex: 20,
            }}
          >
            <button
              role="menuitem"
              onClick={() => { setMenuOpen(false); setEditing(true); }}
              style={menuItemStyle}
            >Edit</button>
            {r.archived ? (
              <button
                role="menuitem"
                onClick={async () => {
                  setMenuOpen(false);
                  await restoreRecipe(r.id);
                  navigate(`/h/${slug.value}/recipes/${r.id}`);
                }}
                style={menuItemStyle}
              >Restore</button>
            ) : (
              <button
                role="menuitem"
                onClick={async () => {
                  setMenuOpen(false);
                  await archiveRecipe(r.id);
                  showToast(`Archived "${r.name}"`, { label: 'Undo', onClick: () => { void restoreRecipe(r.id); } });
                  navigate(`/h/${slug.value}/recipes`);
                }}
                style={{ ...menuItemStyle, color: 'var(--pink-deep)' }}
              >Archive</button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const leftCol = (
    <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: 'var(--space-lg)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
        <StatusTag status={r.status} />
        <PremadeChip visible={r.is_make_ahead} />
      </div>
      <h2 style={{ margin: '4px 0 8px' }}>{r.name}</h2>
      {r.description && <p style={{ color: 'var(--plum-soft)', marginTop: 0 }}>{r.description}</p>}

      {stats.length > 0 && (
        <div className="detail-stat-bar">
          {stats.map(st => (
            <div key={st.label} className="detail-stat">
              <span className="detail-stat-label">{st.label}</span>
              <span className="detail-stat-value">{st.value}</span>
            </div>
          ))}
        </div>
      )}

      <h3>Ingredients</h3>
      <ul style={{ paddingLeft: 18 }}>
        {r.ingredients.map((ing, i) => (
          <li key={i}>{ing.quantity ? `${ing.quantity} ` : ''}{ing.name}</li>
        ))}
      </ul>

      {r.instructions.length > 0 && (<>
        <h3>Instructions</h3>
        <ol style={{ paddingLeft: 22, lineHeight: 1.5 }}>
          {r.instructions.map((step, i) => (
            <li key={i} style={{ marginBottom: 4 }}>{step}</li>
          ))}
        </ol>
      </>)}

      {r.notes && (<>
        <h3>Notes</h3>
        <p>{r.notes}</p>
      </>)}

      {/* On mobile, action buttons live at the bottom of the left column. */}
      {!isWide && actionButtons}
    </div>
  );

  if (isWide) {
    return (
      <Layout active="recipes" slug={slug.value}>
        <AddToPlanModal
          recipeId={r.id}
          open={planOpen}
          initialWeekId={currentWeekId}
          onClose={() => setPlanOpen(false)}
        />
        <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
          <button
            onClick={() => backOr(`/h/${slug.value}/recipes`)}
            style={{ ...backLinkStyle, marginBottom: 12 }}
          >← Back to recipes</button>
          <div className="detail-grid">
            <div>{leftCol}</div>
            <div className="detail-image-col">
              {photoBlock}
              {actionButtons}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout active="recipes" slug={slug.value}>
      <AddToPlanModal
        recipeId={r.id}
        open={planOpen}
        initialWeekId={currentWeekId}
        onClose={() => setPlanOpen(false)}
      />
      <div style={{ padding: 16, maxWidth: 760, margin: '0 auto' }}>
        <button
          onClick={() => backOr(`/h/${slug.value}/recipes`)}
          style={{ ...backLinkStyle, marginBottom: 10 }}
        >← Back to recipes</button>
        {photoBlock}
        <div style={{ marginTop: 12 }}>{leftCol}</div>
      </div>
    </Layout>
  );
}

const btn: JSX.CSSProperties = { padding: '12px 20px', height: 44, borderRadius: 'var(--radius-md)', background: 'var(--pink-primary)', color: 'var(--on-primary)', border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer', lineHeight: 1, fontFamily: 'inherit' };
const secBtn: JSX.CSSProperties = { padding: '12px 20px', height: 44, borderRadius: 'var(--radius-md)', background: 'white', color: 'var(--ink)', border: '1px solid var(--hairline)', fontSize: 14, fontWeight: 600, cursor: 'pointer', lineHeight: 1, fontFamily: 'inherit' };
const menuItemStyle: JSX.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left',
  padding: '10px 12px', borderRadius: 'var(--radius-sm)',
  background: 'transparent', border: 'none', cursor: 'pointer',
  fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit', lineHeight: 1.2,
};
const backLinkStyle: JSX.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  fontSize: 13, color: 'var(--muted)', textDecoration: 'none',
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  fontFamily: 'inherit',
};
